import { apiRequest, refreshSession } from '../api/client';
import { useSessionStore } from '../auth/session-store';

export const INVOICE_STATUSES = [
  'DRAFT',
  'PENDING_ARCA',
  'ISSUED',
  'REJECTED',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_TYPES = ['FACTURA_A', 'FACTURA_B', 'FACTURA_C'] as const;
export type InvoiceType = (typeof INVOICE_TYPES)[number];

/** Matches `apps/api` `StoredInvoiceItem` (`invoice.repository.ts`). */
export interface InvoiceItem {
  id: string;
  deliveryNoteId: string;
  deliveryNoteItemId: string;
  menuId: string;
  descriptionSnapshot: string;
  quantity: number;
  unitPriceCents: number;
  vatRate: number;
  netAmountCents: number;
  vatAmountCents: number;
  siteNameSnapshot: string;
}

export interface InvoiceAddress {
  street?: string;
  city?: string;
  province?: string;
  postalCode?: string;
}

export interface ArcaErrorDetail {
  code: string | null;
  message: string | null;
}

/** Matches `apps/api` `StoredInvoice` (`invoice.repository.ts`). */
export interface Invoice {
  id: string;
  invoiceType: InvoiceType;
  pointOfSaleId: string;
  pointOfSale: number;
  number: number;
  customerId: string;
  contractId: string;
  /** Unit the invoice was drafted under; absent or null on invoices from before the points of sale. */
  operatingUnitId?: string | null;
  operatingUnitName?: string | null;
  status: InvoiceStatus;
  issueDate: string | null;
  dueDate: string | null;
  periodStart: string;
  periodEnd: string;
  customerLegalNameSnapshot: string;
  customerTaxIdSnapshot: string;
  customerTaxConditionSnapshot: string;
  customerAddressSnapshot: InvoiceAddress | null;
  hasIncompleteFiscalData: boolean;
  netAmountCents: number;
  vatAmountCents: number;
  otherTaxesCents: number;
  totalAmountCents: number;
  /** `totalAmountCents` minus allocations of non-voided payments, computed at read time. */
  pendingBalanceCents: number;
  vatBreakdown: Record<string, { netCents: number; vatCents: number }>;
  isTestInvoice: boolean;
  cae: string | null;
  caeExpirationDate: string | null;
  arcaStatus: 'NOT_SENT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  arcaErrorDetail: ArcaErrorDetail | null;
  items: InvoiceItem[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceSearchParams {
  status?: InvoiceStatus | undefined;
  customerId?: string | undefined;
  operatingUnitId?: string | undefined;
  periodStart?: string | undefined;
  periodEnd?: string | undefined;
  page: number;
  pageSize: number;
}

export interface InvoiceSearchResult {
  items: Invoice[];
  page: number;
  pageSize: number;
  total: number;
}

/** The largest page `GET /invoices` accepts (`invoice.dto.ts`'s `listInvoicesQuerySchema`). */
export const MAX_INVOICE_PAGE_SIZE = 100;

/** Matches `apps/api` `GET /invoices`. */
export function listInvoices(params: InvoiceSearchParams): Promise<InvoiceSearchResult> {
  const query = new URLSearchParams();

  if (params.status !== undefined) query.set('status', params.status);
  if (params.customerId !== undefined) query.set('customerId', params.customerId);
  if (params.operatingUnitId !== undefined) query.set('operatingUnitId', params.operatingUnitId);
  if (params.periodStart !== undefined) query.set('periodStart', params.periodStart);
  if (params.periodEnd !== undefined) query.set('periodEnd', params.periodEnd);
  query.set('page', String(params.page));
  query.set('pageSize', String(Math.min(params.pageSize, MAX_INVOICE_PAGE_SIZE)));

  return apiRequest<InvoiceSearchResult>(`/invoices?${query.toString()}`);
}

export type FiscalEnvironmentValue = 'simulado' | 'homologacion' | 'produccion';

export interface FiscalMode {
  isTestMode: boolean;
  /** Absent on older API deployments — callers fall back to a generic banner. */
  environment?: FiscalEnvironmentValue;
}

/** Matches `apps/api` `GET /invoices/fiscal-mode`. */
export function getFiscalMode(): Promise<FiscalMode> {
  return apiRequest<FiscalMode>('/invoices/fiscal-mode');
}

/** Matches `apps/api` `GET /invoices/:id`. */
export function getInvoice(id: string): Promise<Invoice> {
  return apiRequest<Invoice>(`/invoices/${id}`);
}

export interface GeneratedInvoiceSummary {
  id: string;
  customerId: string;
  contractId: string;
  invoiceType: InvoiceType;
  periodStart: string;
  periodEnd: string;
  totalAmountCents: number;
  hasIncompleteFiscalData: boolean;
}

export type SkippedOperatingUnitReason =
  | 'OPERATING_UNIT_WITHOUT_FISCAL_POINT_OF_SALE'
  | 'NO_FISCAL_POINT_OF_SALE_AVAILABLE';

/** A unit left unbilled by a draft run because it has no usable fiscal point of sale. */
export interface SkippedOperatingUnit {
  operatingUnitId: string;
  operatingUnitName: string;
  reason: SkippedOperatingUnitReason;
}

export interface GenerateBillingDraftResult {
  invoices: GeneratedInvoiceSummary[];
  skippedOperatingUnits: SkippedOperatingUnit[];
}

/** Matches `apps/api` `POST /invoices/draft`. */
export function generateBillingDraft(
  periodStart: string,
  periodEnd: string,
  customerIds?: string[],
): Promise<GenerateBillingDraftResult> {
  return apiRequest<GenerateBillingDraftResult>('/invoices/draft', {
    method: 'POST',
    body: JSON.stringify({ periodStart, periodEnd, ...(customerIds ? { customerIds } : {}) }),
  });
}

/** Matches `apps/api` `POST /invoices/:id/exclude-item`. */
export function excludeInvoiceItem(
  id: string,
  deliveryNoteId: string,
  reason: string,
): Promise<Invoice> {
  return apiRequest<Invoice>(`/invoices/${id}/exclude-item`, {
    method: 'POST',
    body: JSON.stringify({ deliveryNoteId, reason }),
  });
}

/** Matches `apps/api` `POST /invoices/:id/revert-to-draft`. */
export function revertInvoiceToDraft(id: string): Promise<Invoice> {
  return apiRequest<Invoice>(`/invoices/${id}/revert-to-draft`, { method: 'POST' });
}

/** Matches `apps/api` `POST /invoices/:id/issue`. */
export function issueInvoice(id: string): Promise<Invoice> {
  return apiRequest<Invoice>(`/invoices/${id}/issue`, { method: 'POST' });
}

/** Matches `apps/api` `POST /invoices/:id/reconcile`. */
export function reconcileInvoice(id: string): Promise<Invoice> {
  return apiRequest<Invoice>(`/invoices/${id}/reconcile`, { method: 'POST' });
}

export interface BatchIssuanceOutcome {
  invoiceId: string;
  outcome: 'ISSUED' | 'REJECTED' | 'SKIPPED';
  reason?: string;
  /** Present only when the invoice already has a number (issued or reserved by ARCA). */
  pointOfSale?: number;
  number?: number;
}

/** Matches `apps/api` `POST /invoices/issue-batch`, invoked with an explicit selection ("Emitir seleccionadas"), never a period range — the UI never exposes the period-based batch mode `issueInvoicesBatchSchema` also accepts. */
export function issueInvoicesBatch(invoiceIds: string[]): Promise<BatchIssuanceOutcome[]> {
  return apiRequest<BatchIssuanceOutcome[]>('/invoices/issue-batch', {
    method: 'POST',
    body: JSON.stringify({ invoiceIds }),
  });
}

export interface ArcaRequestLog {
  id: string;
  operation: string;
  requestPayload: unknown;
  responsePayload: unknown;
  httpStatus: number | null;
  success: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  attemptNumber: number;
  durationMs: number;
  createdAt: string;
}

/** Matches `apps/api` `GET /invoices/:id/arca-logs`. */
export function getInvoiceArcaLogs(id: string): Promise<ArcaRequestLog[]> {
  return apiRequest<ArcaRequestLog[]>(`/invoices/${id}/arca-logs`);
}

function sendBlobRequest(path: string, init: RequestInit): Promise<Response> {
  const token = useSessionStore.getState().session?.accessToken;
  const headers = new Headers(init.headers);

  if (token !== undefined) {
    headers.set('authorization', `Bearer ${token}`);
  }
  if (init.body !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  return fetch(`/api/v1${path}`, { ...init, headers, credentials: 'include' });
}

/**
 * Same single-flight 401-refresh-and-retry pattern as
 * `delivery-notes/api.ts`'s `downloadPdf`: a bare `fetch()` would leave a
 * concurrently rotated access token as an unrecovered 401.
 */
async function downloadPdf(path: string, filename: string): Promise<void> {
  let response = await sendBlobRequest(path, { method: 'GET' });

  if (response.status === 401) {
    const renewed = await refreshSession();

    if (renewed) {
      response = await sendBlobRequest(path, { method: 'GET' });
    }
  }

  if (!response.ok) {
    throw new Error('No pudimos generar el PDF.');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();

  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Matches `apps/api` `GET /invoices/:id/pdf`. */
export function downloadInvoicePdf(id: string, invoiceNumber: string): Promise<void> {
  return downloadPdf(`/invoices/${id}/pdf`, `factura-${invoiceNumber}.pdf`);
}
